import { defineStore } from "pinia";
import { useGeneralStore } from "%/stores/general-store";
import { useMyNetworkFeedStore } from "@/smart-feed/stores/feed/my-network-feed-store";
import { useCommunityNetworkFeedStore } from "@/smart-feed/stores/feed/community-network-feed-store";
import { useNetworkApplicantsAppliedFeedStore } from "@/smart-feed/stores/feed/network-applicants-applied-feed-store";
import { useNetworkApplicantsInvitedFeedStore } from "@/smart-feed/stores/feed/network-applicants-invited-feed-store";
import { DEFAULT_ORG_LOGO, DEFAULT_SPLASH_IMAGE } from "%/constants/publication-constants";
import { useNetworkPublicationsFeedStore } from "@/smart-feed/stores/feed/network-publications-feed-store";

export const useNetworkStore = defineStore("network-store", {
  state: () => ({
    isLoadingCreateNetwork: false,
    isLoadingEditNetwork: false,
    network: null,
    networkModalOpen: false,
    networkModalMode: "create",
    currentNetwork: null,
    isLoadingInvitePublication: false,
    isLoadingApplyToNetwork: false,
    isLoadingAcceptInvite: false,
    isLoadingDeclineInvite: false,
    isLoadingRevokeInvite: false,
    isLoadingAcceptApplication: false,
    isLoadingRejectApplication: false,
    isLoadingRemovePublication: false,
    isLoadingAddPublicationToNetwork: false,
    applyToNetworkModalOpen: false,
    invitePublicationModalOpen: false,
    addToNetworkModalOpen: false,
    reviewApplicationModalOpen: false,
    reviewInviteModalOpen: false,
    selectedInvitePublication: null,
    selectedApplication: null,
    selectedInvite: null,
    selectedPublicationId: null,
    ownedNetworks: [],
    isLoadingUploadLogo: false,
    isLoadingUploadSplashImage: false,
    isLoadingSaveNetworkName: false,
    isLoadingSaveNetworkDescription: false,
    error: null,
  }),
  getters: {
    orgId() {
      return useGeneralStore().orgId;
    },
    userId() {
      return useGeneralStore().userId;
    },
    networkId() {
      return this.network.network_id;
    },
    networkName(state) {
      return state.network?.name || "";
    },
    networkDescription(state) {
      return state.network?.description ?? "";
    },
    networkLogo(state) {
      return state.network?.logo_url || DEFAULT_ORG_LOGO;
    },
    networkSplashImage(state) {
      return state.network?.splash_image_url || DEFAULT_SPLASH_IMAGE;
    },
  },
  actions: {
    openNetworkModal(mode = "create", network = null) {
      this.networkModalMode = mode;
      this.currentNetwork = network;
      this.networkModalOpen = true;
    },

    closeNetworkModal() {
      this.networkModalOpen = false;
      this.currentNetwork = null;
    },

    openApplyToNetworkModal() {
      this.applyToNetworkModalOpen = true;
      this.error = null;
    },

    closeApplyToNetworkModal() {
      this.applyToNetworkModalOpen = false;
    },

    openInvitePublicationModal(data = null) {
      this.invitePublicationModalOpen = true;
      this.selectedInvitePublication = data;
      this.error = null;
    },

    closeInvitePublicationModal() {
      this.invitePublicationModalOpen = false;
      this.selectedInvitePublication = null;
    },

    openReviewApplicationModal(application) {
      this.selectedApplication = application;
      this.reviewApplicationModalOpen = true;
    },

    closeReviewApplicationModal() {
      this.reviewApplicationModalOpen = false;
      this.selectedApplication = null;
    },

    openReviewInviteModal(invite) {
      this.selectedInvite = invite;
      this.reviewInviteModalOpen = true;
    },

    closeReviewInviteModal() {
      this.reviewInviteModalOpen = false;
      this.selectedInvite = null;
    },

    openAddToNetworkModal(publicationId = null) {
      this.selectedPublicationId = publicationId;
      this.addToNetworkModalOpen = true;
      this.error = null;
    },

    closeAddToNetworkModal() {
      this.addToNetworkModalOpen = false;
      this.selectedPublicationId = null;
    },

    addPublicationToNetworks(networkIds, publicationId, onSuccess = () => {}) {
      this.isLoadingAddPublicationToNetwork = true;
      this.error = null;
      return this.apiPost(
        "/network/publication/add",
        {
          network_ids: networkIds,
          publication_id: publicationId,
        },
        () => {
          onSuccess();
        },
        error => {
          this.error =
            error.response?.data?.message || "Failed to add publication to network";
        },
        () => {
          this.isLoadingAddPublicationToNetwork = false;
        },
      );
    },

    createNetwork(
      name,
      description,
      inviteOnly = false,
      logoUrl = "",
      splashImageUrl = "",
      price = 0,
      onSuccess = () => {},
    ) {
      this.isLoadingCreateNetwork = true;
      this.error = null;

      this.apiPost(
        "/network",
        {
          name,
          description,
          invite_only: inviteOnly,
          logo_url: logoUrl,
          splash_image_url: splashImageUrl,
          price,
          billing_frequency: "monthly",
        },
        () => {
          this.isLoadingCreateNetwork = false;
          onSuccess();
          useMyNetworkFeedStore().reload();
          useCommunityNetworkFeedStore().reload();
        },
        error => {
          this.isLoadingCreateNetwork = false;
          this.error = error.response?.data?.message || "Failed to create network";
        },
      );
    },

    saveNetworkName(networkId, name, finalHandler = () => {}) {
      this.isLoadingSaveNetworkName = true;
      this.apiPost(
        `/network/${networkId}/name`,
        { name },
        data => {
          if (this.network) {
            this.network.name = data.network.name;
          }
        },
        () => {},
        () => {
          this.isLoadingSaveNetworkName = false;
          finalHandler();
        },
      );
    },

    saveNetworkDescription(networkId, description, finalHandler = () => {}) {
      this.isLoadingSaveNetworkDescription = true;
      this.apiPost(
        `/network/${networkId}/description`,
        { description },
        data => {
          if (this.network) {
            this.network.description = data.network.description;
          }
        },
        () => {},
        () => {
          this.isLoadingSaveNetworkDescription = false;
          finalHandler();
        },
      );
    },

    invitePublication(networkId, publicationId, orgId, userId, owningOrgId, message, onSuccess = () => {}) {
      this.isLoadingInvitePublication = true;
      this.error = null;

      this.apiPost(
        `/network/${networkId}/${publicationId}/invite`,
        {
          org_id: orgId,
          user_id: userId,
          owning_org_id: owningOrgId,
          message,
        },
        () => {
          this.isLoadingInvitePublication = false;
          onSuccess();
        },
        error => {
          this.isLoadingInvitePublication = false;
          this.error = error.response?.data?.message || "Failed to invite publication";
        },
      );
    },

    applyToNetwork(networkId, publicationId, orgId, userId, message, onSuccess = () => {}) {
      this.isLoadingApplyToNetwork = true;
      this.error = null;

      this.apiPost(
        `/network/${networkId}/${publicationId}/apply`,
        {
          org_id: orgId,
          user_id: userId,
          message,
        },
        () => {
          this.isLoadingApplyToNetwork = false;
          onSuccess();
        },
        error => {
          this.isLoadingApplyToNetwork = false;
          this.error = error.response?.data?.message || "Failed to apply to network";
        },
      );
    },

    acceptInvite(networkId, publicationId) {
      this.isLoadingAcceptInvite = true;
      this.error = null;

      this.apiPost(
        `/network/${networkId}/${publicationId}/invite/accept`,
        {},
        () => {
          this.isLoadingAcceptInvite = false;
        },
        error => {
          this.isLoadingAcceptInvite = false;
          this.error = error.response?.data?.message || "Failed to accept invite";
        },
      );
    },

    revokeInvite(networkId, publicationId) {
      this.isLoadingRevokeInvite = true;
      this.error = null;

      this.apiPost(
        `/network/${networkId}/${publicationId}/invite/revoke`,
        {},
        () => {
          this.isLoadingRevokeInvite = false;
          useNetworkApplicantsInvitedFeedStore().reload();
        },
        error => {
          this.isLoadingRevokeInvite = false;
          this.error = error.response?.data?.message || "Failed to revoke invite";
        },
      );
    },

    declineInvite(networkId, publicationId) {
      this.isLoadingDeclineInvite = true;
      this.error = null;

      this.apiPost(
        `/network/${networkId}/${publicationId}/invite/decline`,
        {},
        () => {
          this.isLoadingDeclineInvite = false;
        },
        error => {
          this.isLoadingDeclineInvite = false;
          this.error = error.response?.data?.message || "Failed to decline invite";
        },
      );
    },

    acceptApplication(networkId, publicationId) {
      this.isLoadingAcceptApplication = true;
      this.error = null;

      this.apiPost(
        `/network/${networkId}/${publicationId}/application/accept`,
        {},
        () => {
          this.isLoadingAcceptApplication = false;
          this.closeReviewApplicationModal();
          useNetworkApplicantsAppliedFeedStore().reload();
        },
        error => {
          this.isLoadingAcceptApplication = false;
          this.error = error.response?.data?.message || "Failed to accept application";
        },
      );
    },

    rejectApplication(networkId, publicationId) {
      this.isLoadingRejectApplication = true;
      this.error = null;

      this.apiPost(
        `/network/${networkId}/${publicationId}/application/reject`,
        {},
        () => {
          this.isLoadingRejectApplication = false;
          this.closeReviewApplicationModal();
          useNetworkApplicantsAppliedFeedStore().reload();
        },
        error => {
          this.isLoadingRejectApplication = false;
          this.error = error.response?.data?.message || "Failed to reject application";
        },
      );
    },

    removePublication(networkId, publicationId) {
      this.isLoadingRemovePublication = true;
      this.error = null;

      this.apiPost(
        `/network/${networkId}/${publicationId}/remove`,
        {},
        () => {
          this.isLoadingRemovePublication = false;
          useNetworkPublicationsFeedStore().reload();
        },
        error => {
          this.isLoadingRemovePublication = false;
          this.error = error.response?.data?.message || "Failed to remove publication";
        },
      );
    },

    enableApplications(networkId) {
      this.isLoadingEditNetwork = true;
      this.error = null;

      this.apiPost(
        `/network/${networkId}/applications/enable`,
        {},
        () => {
          this.isLoadingEditNetwork = false;
          if (this.network) {
            this.network.invite_only = false;
          }
        },
        error => {
          this.isLoadingEditNetwork = false;
          this.error = error.response?.data?.message || "Failed to enable applications";
        },
      );
    },

    disableApplications(networkId) {
      this.isLoadingEditNetwork = true;
      this.error = null;

      this.apiPost(
        `/network/${networkId}/applications/disable`,
        {},
        () => {
          this.isLoadingEditNetwork = false;
          if (this.network) {
            this.network.invite_only = true;
          }
        },
        error => {
          this.isLoadingEditNetwork = false;
          this.error = error.response?.data?.message || "Failed to disable applications";
        },
      );
    },

    uploadLogo(file, onSuccess = () => {}, onError = () => {}) {
      let formData = new FormData();
      formData.append("logo", file);
      formData.append("network_id", this.network?.network_id || 0);
      this.isLoadingUploadLogo = true;
      return this.apiPost(
        "/network/logo/upload",
        formData,
        response => {
          this.isLoadingUploadLogo = false;
          if (this.network) {
            this.network.logo_url = response.url;
          }
          onSuccess(response.url);
        },
        error => {
          this.isLoadingUploadLogo = false;
          onError(error);
        },
      );
    },

    uploadSplashImage(file, onSuccess = () => {}, onError = () => {}) {
      let formData = new FormData();
      formData.append("splash", file);
      formData.append("network_id", this.network?.network_id || 0);
      this.isLoadingUploadSplashImage = true;

      return this.apiPost(
        "/network/splash/upload",
        formData,
        response => {
          this.isLoadingUploadSplashImage = false;
          if (this.network) {
            this.network.splash_image_url = response.url;
          }
          onSuccess(response.url);
        },
        error => {
          this.isLoadingUploadSplashImage = false;
          onError(error);
        },
      );
    },
  },
});

